import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Chip,
  Grid,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import { ShowChart } from '@mui/icons-material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { apiService, TradingSignal } from '../services/api';
import { useTheme } from '@mui/material/styles';

const TradingSignals: React.FC = () => {
  const theme = useTheme();
  const [symbol, setSymbol] = useState('');
  const [duration, setDuration] = useState('6 M');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [signalData, setSignalData] = useState<TradingSignal | null>(null);

  const handleGetSignals = async () => {
    if (!symbol) return;

    setLoading(true);
    setError(null);

    try {
      const data = await apiService.getTradingSignals(symbol.toUpperCase(), duration);
      setSignalData(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to fetch signals');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <ShowChart />
        <Typography variant="h4" fontWeight={700}>
          Trading Signals
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Technical indicator-based buy/sell signals with historical performance
      </Typography>

      {/* Search Form */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-end">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Stock Symbol"
                value={symbol}
                onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                onKeyPress={(e) => e.key === 'Enter' && handleGetSignals()}
                placeholder="AAPL"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Lookback Period</InputLabel>
                <Select
                  value={duration}
                  onChange={(e) => setDuration(e.target.value)}
                  label="Lookback Period"
                >
                  <MenuItem value="1 D">1 Day</MenuItem>
                  <MenuItem value="1 W">1 Week</MenuItem>
                  <MenuItem value="1 M">1 Month</MenuItem>
                  <MenuItem value="3 M">3 Months</MenuItem>
                  <MenuItem value="6 M">6 Months</MenuItem>
                  <MenuItem value="1 Y">1 Year</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleGetSignals}
                disabled={loading || !symbol}
                sx={{ height: '56px' }}
              >
                Get Signals
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Loading / Error */}
      {loading && <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />}
      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {/* Signal Results */}
      {signalData && !loading && (
        <>
          {/* Agentic Analysis Summary */}
          <Card sx={{ mb: 3, bgcolor: theme.palette.mode === 'dark' ? '#1a1a1a' : '#f5f5f5' }}>
            <CardContent>
              <Typography variant="h5" gutterBottom fontWeight={700}>
                {signalData.symbol} - Analysis Summary
              </Typography>

              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary">
                      Current Price
                    </Typography>
                    <Typography variant="h4" fontWeight={700}>
                      ${signalData.latest_signals.close.toFixed(2)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(signalData.latest_signals.date).toLocaleDateString()}
                    </Typography>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper
                    sx={{
                      p: 2,
                      bgcolor: (signalData.latest_signals as any).recommendation_color === 'success'
                        ? theme.palette.success.main + '20'
                        : (signalData.latest_signals as any).recommendation_color === 'error'
                        ? theme.palette.error.main + '20'
                        : 'background.default',
                      border: '2px solid',
                      borderColor: (signalData.latest_signals as any).recommendation_color === 'success'
                        ? theme.palette.success.main
                        : (signalData.latest_signals as any).recommendation_color === 'error'
                        ? theme.palette.error.main
                        : theme.palette.grey[400],
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                      RECOMMENDATION
                    </Typography>
                    <Typography
                      variant="h4"
                      fontWeight={700}
                      color={(signalData.latest_signals as any).recommendation_color || 'text.primary'}
                      sx={{ mt: 1 }}
                    >
                      {(signalData.latest_signals as any).recommendation || 'HOLD'}
                    </Typography>
                    {signalData.target_price && (
                      <>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                          Target: ${signalData.target_price.toFixed(2)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Stop: ${signalData.stop_loss?.toFixed(2) || 'N/A'}
                        </Typography>
                      </>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Paper sx={{ p: 2, bgcolor: 'background.default' }}>
                    <Typography variant="caption" color="text.secondary">
                      Signal Scores
                    </Typography>
                    <Box sx={{ mt: 1, display: 'flex', gap: 2 }}>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="success.main">
                          {(signalData.latest_signals as any).bullish_score || 0}
                        </Typography>
                        <Typography variant="caption">Bullish</Typography>
                      </Box>
                      <Typography variant="h5" color="text.secondary">/</Typography>
                      <Box>
                        <Typography variant="h5" fontWeight={700} color="error.main">
                          {(signalData.latest_signals as any).bearish_score || 0}
                        </Typography>
                        <Typography variant="caption">Bearish</Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      (out of 100)
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>

              {/* Price Targets Section */}
              {signalData.target_price && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                    Price Targets
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
                        <Typography variant="caption">Target Price</Typography>
                        <Typography variant="h5" fontWeight={700}>
                          ${signalData.target_price.toFixed(2)}
                        </Typography>
                        {signalData.target_timeframe && (
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            {signalData.target_timeframe}
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 2, bgcolor: signalData.expected_return_pct !== undefined && signalData.expected_return_pct > 0 ? 'success.main' : 'error.main', color: 'white' }}>
                        <Typography variant="caption">Expected Return</Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {signalData.expected_return_pct !== undefined ? `${signalData.expected_return_pct > 0 ? '+' : ''}${signalData.expected_return_pct.toFixed(2)}%` : 'N/A'}
                        </Typography>
                        <Typography variant="caption" sx={{ opacity: 0.9 }}>
                          Potential gain/loss
                        </Typography>
                      </Paper>
                    </Grid>
                    {signalData.stop_loss && (
                      <Grid item xs={12} md={3}>
                        <Paper sx={{ p: 2, bgcolor: 'error.main', color: 'error.contrastText' }}>
                          <Typography variant="caption">Stop Loss</Typography>
                          <Typography variant="h5" fontWeight={700}>
                            ${signalData.stop_loss.toFixed(2)}
                          </Typography>
                          <Typography variant="caption" sx={{ opacity: 0.9 }}>
                            Risk limit: {((Math.abs(signalData.stop_loss - signalData.latest_signals.close) / signalData.latest_signals.close) * 100).toFixed(1)}%
                          </Typography>
                        </Paper>
                      </Grid>
                    )}
                    <Grid item xs={12} md={3}>
                      <Paper sx={{ p: 2, bgcolor: 'background.default', border: '2px dashed', borderColor: 'divider' }}>
                        <Typography variant="caption" color="text.secondary">
                          Risk/Reward Ratio
                        </Typography>
                        <Typography variant="h5" fontWeight={700}>
                          {signalData.stop_loss && signalData.expected_return_pct !== undefined
                            ? (Math.abs(signalData.expected_return_pct) / ((Math.abs(signalData.stop_loss - signalData.latest_signals.close) / signalData.latest_signals.close) * 100)).toFixed(2)
                            : 'N/A'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {signalData.stop_loss && signalData.expected_return_pct !== undefined && (Math.abs(signalData.expected_return_pct) / ((Math.abs(signalData.stop_loss - signalData.latest_signals.close) / signalData.latest_signals.close) * 100)) >= 2
                            ? '✓ Good ratio'
                            : 'Caution advised'}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Analysis Points */}
              {(signalData.latest_signals as any).analysis && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                    Technical Analysis
                  </Typography>
                  {(signalData.latest_signals as any).analysis.map((point: string, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                      {point}
                    </Typography>
                  ))}
                </Box>
              )}

              {/* Timeframe Metadata */}
              {signalData.lookback_period && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'background.default', borderRadius: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      Signal Timeframe
                    </Typography>
                    <Tooltip title="Trading signals are generated based on technical indicators analyzed over the lookback period. The validity period indicates how long the signal is expected to remain relevant before needing re-evaluation.">
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                  <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Lookback Period
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {signalData.lookback_period}
                      </Typography>
                    </Grid>
                    <Grid item xs={6} md={3}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Signal Valid For
                      </Typography>
                      <Chip
                        label={`${signalData.signal_validity_days || 7} days`}
                        color="primary"
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </Grid>
                    {signalData.signal_strength !== undefined && (
                      <Grid item xs={6} md={3}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Signal Strength
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress
                            variant="determinate"
                            value={signalData.signal_strength * 10}
                            sx={{ width: 80, height: 8, borderRadius: 4 }}
                            color={signalData.signal_strength >= 7 ? 'success' : signalData.signal_strength >= 4 ? 'warning' : 'error'}
                          />
                          <Typography variant="body2" fontWeight={600}>
                            {signalData.signal_strength}/10
                          </Typography>
                        </Box>
                      </Grid>
                    )}
                    {signalData.data_start_date && signalData.data_end_date && (
                      <Grid item xs={12} md={3}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Data Range
                        </Typography>
                        <Typography variant="body2" fontWeight={500}>
                          {signalData.data_start_date} to {signalData.data_end_date}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Historical Signals (6M): {signalData.buy_signals.length} Buy • {signalData.sell_signals.length} Sell
                </Typography>
              </Box>
            </CardContent>
          </Card>

          {/* Current Signal Status */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight={600}>
                Detailed Indicator Breakdown
              </Typography>

              {/* Comprehensive Analysis Sections */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Signal Strength
                </Typography>
                <Grid container spacing={2}>
                  <Grid item>
                    <Chip
                      label={`Bullish Score: ${(signalData.latest_signals as any).bullish_score || 0}/100`}
                      color={(signalData.latest_signals as any).bullish_score >= 50 ? 'success' : (signalData.latest_signals as any).bullish_score >= 30 ? 'warning' : 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Grid>
                  <Grid item>
                    <Chip
                      label={`Bearish Score: ${(signalData.latest_signals as any).bearish_score || 0}/100`}
                      color={(signalData.latest_signals as any).bearish_score >= 50 ? 'error' : (signalData.latest_signals as any).bearish_score >= 30 ? 'warning' : 'default'}
                      sx={{ fontWeight: 600 }}
                    />
                  </Grid>
                  {(signalData.latest_signals as any).hold_signal && (
                    <Grid item>
                      <Chip label="HOLD" color="default" variant="outlined" sx={{ fontWeight: 600 }} />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Trend Analysis
                </Typography>
                <Grid container spacing={2}>
                  <Grid item>
                    <Chip label={`Trend: ${(signalData.latest_signals as any).trend || 'Unknown'}`} />
                  </Grid>
                  {(signalData.latest_signals as any).price_above_sma20 !== undefined && (
                    <Grid item>
                      <Chip
                        label="Above SMA20"
                        color={(signalData.latest_signals as any).price_above_sma20 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Grid>
                  )}
                  {(signalData.latest_signals as any).price_above_sma50 !== undefined && (
                    <Grid item>
                      <Chip
                        label="Above SMA50"
                        color={(signalData.latest_signals as any).price_above_sma50 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Grid>
                  )}
                  {(signalData.latest_signals as any).price_above_sma200 !== undefined && (
                    <Grid item>
                      <Chip
                        label="Above SMA200"
                        color={(signalData.latest_signals as any).price_above_sma200 ? 'success' : 'error'}
                        variant="outlined"
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Momentum Indicators
                </Typography>
                <Grid container spacing={2}>
                  {signalData.latest_signals.rsi !== undefined && (
                    <Grid item>
                      <Chip
                        label={`RSI: ${signalData.latest_signals.rsi.toFixed(1)} (${(signalData.latest_signals as any).rsi_status || 'Neutral'})`}
                        color={
                          signalData.latest_signals.rsi < 30
                            ? 'success'
                            : signalData.latest_signals.rsi > 70
                            ? 'error'
                            : 'default'
                        }
                      />
                    </Grid>
                  )}
                  {signalData.latest_signals.macd !== undefined && (
                    <Grid item>
                      <Chip
                        label={`MACD: ${signalData.latest_signals.macd.toFixed(3)} (${(signalData.latest_signals as any).macd_status || 'Neutral'})`}
                        color={signalData.latest_signals.macd > 0 ? 'success' : 'error'}
                      />
                    </Grid>
                  )}
                  {(signalData.latest_signals as any).stoch_k !== undefined && (
                    <Grid item>
                      <Chip
                        label={`Stoch: ${(signalData.latest_signals as any).stoch_k.toFixed(1)}`}
                        color={
                          (signalData.latest_signals as any).stoch_k < 20
                            ? 'success'
                            : (signalData.latest_signals as any).stoch_k > 80
                            ? 'error'
                            : 'default'
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom fontWeight={600}>
                  Volatility & Volume
                </Typography>
                <Grid container spacing={2}>
                  {signalData.latest_signals.bb_position && (
                    <Grid item>
                      <Chip
                        label={`BB: ${signalData.latest_signals.bb_position} (${((signalData.latest_signals as any).bb_position_pct || 50).toFixed(0)}%)`}
                        color={
                          signalData.latest_signals.bb_position.includes('Lower') || signalData.latest_signals.bb_position.includes('Below')
                            ? 'success'
                            : signalData.latest_signals.bb_position.includes('Upper') || signalData.latest_signals.bb_position.includes('Above')
                            ? 'error'
                            : 'default'
                        }
                      />
                    </Grid>
                  )}
                  {(signalData.latest_signals as any).bb_squeeze && (
                    <Grid item>
                      <Chip label="BB Squeeze" color="warning" variant="outlined" />
                    </Grid>
                  )}
                  {(signalData.latest_signals as any).volume_status && (
                    <Grid item>
                      <Chip
                        label={`Volume: ${(signalData.latest_signals as any).volume_status}`}
                        color={
                          (signalData.latest_signals as any).volume_status === 'Extreme Volume' || (signalData.latest_signals as any).volume_status === 'High Volume'
                            ? 'success'
                            : (signalData.latest_signals as any).volume_status === 'Low Volume'
                            ? 'error'
                            : 'default'
                        }
                      />
                    </Grid>
                  )}
                </Grid>
              </Box>
            </CardContent>
          </Card>

          {/* Buy Signals History */}
          {signalData.buy_signals.length > 0 && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600} color="success.main">
                  Buy Signals ({signalData.buy_signals.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Signal Type</TableCell>
                        <TableCell align="right">RSI</TableCell>
                        <TableCell align="right">MACD</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {signalData.buy_signals.slice(0, 10).map((signal, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{new Date(signal.date).toLocaleDateString()}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              ${((signal as any).price || (signal as any).close)?.toFixed(2) || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={signal.signal_type || 'Combined'} size="small" color="success" />
                          </TableCell>
                          <TableCell align="right">{signal.rsi?.toFixed(2) || '-'}</TableCell>
                          <TableCell align="right">{signal.macd?.toFixed(4) || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {/* Sell Signals History */}
          {signalData.sell_signals.length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom fontWeight={600} color="error.main">
                  Sell Signals ({signalData.sell_signals.length})
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Price</TableCell>
                        <TableCell>Signal Type</TableCell>
                        <TableCell align="right">RSI</TableCell>
                        <TableCell align="right">MACD</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {signalData.sell_signals.slice(0, 10).map((signal, idx) => (
                        <TableRow key={idx} hover>
                          <TableCell>{new Date(signal.date).toLocaleDateString()}</TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600}>
                              ${((signal as any).price || (signal as any).close)?.toFixed(2) || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip label={signal.signal_type || 'Combined'} size="small" color="error" />
                          </TableCell>
                          <TableCell align="right">{signal.rsi?.toFixed(2) || '-'}</TableCell>
                          <TableCell align="right">{signal.macd?.toFixed(4) || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          )}

          {signalData.buy_signals.length === 0 && signalData.sell_signals.length === 0 && (
            <Alert severity="info">
              No trading signals detected in the last 6 months for {signalData.symbol}
            </Alert>
          )}
        </>
      )}

    </Box>
  );
};

export default TradingSignals;
